import { AxiosError } from 'axios';

import {
    Button,
    Group,
    Stack,
    Table,
    Text,
    Title,
    Tooltip
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';

import {
    useGetApiAdminSettingsOptions,
    usePostApiAdminSettingsClean,
    usePostApiAdminSettingsSeedDev
} from '../../api/admin-settings/admin-settings';
import { DatabaseAdminOperation } from '../../api/models';
import { useModal } from '../utils/useModal.tsx';
import ArchivedProjectsModal from './ArchivedProjectsModal.tsx';

interface AdminOperationsProps {
    refetch: () => void;
}

export const AdminOperations = ({ refetch }: AdminOperationsProps) => {
    const [
        archivedModalOpened,
        { open: openArchivedModal, close: closeArchivedModal }
    ] = useDisclosure(false);
    const { data: adminOptions } = useGetApiAdminSettingsOptions();
    const {
        mutateAsync: cleanDatabaseAsync,
        isPending: isDatabaseCleanLoading
    } = usePostApiAdminSettingsClean();
    const { mutateAsync: devSeedDatabaseAsync, isPending: isDevSeedLoading } =
        usePostApiAdminSettingsSeedDev();
    const { showServerErrorModal } = useModal();

    if (!adminOptions)
        return <Text>Failed loading required admin options metadata.</Text>;

    const isModifyingDb = isDevSeedLoading || isDatabaseCleanLoading;

    const clean = 'clean';
    const devSeed = 'seed/dev';

    const handleModifyDatabaseRequest = (
        modes: string[],
        description: string
    ) => {
        modals.openConfirmModal({
            size: 'lg',
            padding: 'md',
            withCloseButton: false,
            children: (
                <Stack gap={'md'}>
                    <Title order={4}>
                        Are you sure you want to modify the database?
                    </Title>
                    <Text style={{ whiteSpace: 'pre-wrap' }}>
                        {description}
                    </Text>
                </Stack>
            ),
            labels: { confirm: 'Proceed', cancel: 'Cancel' },
            onCancel: () => {
                return;
            },
            onConfirm: async () => {
                await modifyDatabase(modes);
                refetch();
            }
        });
    };

    const modifyDatabase = async (modes: string[]) => {
        if (modes.includes(clean)) {
            await cleanDatabaseAsync().catch((error: Error | AxiosError) => {
                showServerErrorModal('Failed cleaning database', error);
                return;
            });
        }

        if (modes.includes(devSeed)) {
            await devSeedDatabaseAsync().catch((error: Error | AxiosError) => {
                showServerErrorModal('Failed seeding database', error);
                return;
            });
        }
    };

    const getOpsSectionRow = (title: string, content: JSX.Element) => {
        return (
            <Table.Tr>
                <Table.Td w={100}>
                    <Text fw={500} c={'dimmed'}>
                        {title}
                    </Text>
                </Table.Td>
                <Table.Td>
                    <Group gap={'md'}>{content}</Group>
                </Table.Td>
            </Table.Tr>
        );
    };

    const getActionButton = (title: string, modes: string[]) => {
        const modeDescriptions: string[] = [];

        for (const mode of modes) {
            const option = adminOptions.find(
                (o: DatabaseAdminOperation) => o.modeName === mode
            );
            // we want a clear description for each action, so
            // if we can't look up one of the mode's description, then
            // we won't be rendering the button.
            if (option === undefined) return <></>;

            modeDescriptions.push(option.description);
        }

        const description = modeDescriptions.join('\n\n');

        if (description.length < 1) return <></>;

        return (
            <Tooltip
                label={description}
                multiline
                w={'500px'}
                style={{ whiteSpace: 'pre-wrap' }}
            >
                <Button
                    onClick={() =>
                        handleModifyDatabaseRequest(modes, description)
                    }
                    loading={isModifyingDb}
                >
                    {title}
                </Button>
            </Tooltip>
        );
    };

    return (
        <Stack gap={'md'}>
            <Table verticalSpacing='md' withRowBorders withColumnBorders>
                <Table.Tbody>
                    {getOpsSectionRow(
                        'General',
                        <Group gap={'md'}>
                            {getActionButton('Clean and Reseed Database', [
                                clean,
                                devSeed
                            ])}
                            {getActionButton('Clean Database', [clean])}
                        </Group>
                    )}
                    {getOpsSectionRow(
                        'Recovery',
                        <Group gap={'md'}>
                            <Button onClick={openArchivedModal}>
                                Archived Projects
                            </Button>
                        </Group>
                    )}
                </Table.Tbody>
            </Table>
            <ArchivedProjectsModal
                opened={archivedModalOpened}
                onClose={closeArchivedModal}
            />
        </Stack>
    );
};
